import { POST_LOGIN_PATHS } from '../constants/postLoginPaths';

// Server-rejected session recovery (Fable review 2026-09-23, item B).
//
// The store.js fetch interceptor only ever judged the stored token by its
// client-side `exp`. A token the SERVER rejects while still unexpired (a JWT
// secret rotation, a tokenVersion bump after a password/email/phone change)
// left the user signed-in-looking but failing every call until `exp`.
//
// A 401 alone is NOT proof the token is dead. The API returns 401 with a
// perfectly valid token for business reasons (a non-tutor asking for tutor
// feedback, a notes-to-quiz link assigned to another email, a QLink share
// token that does not match) and for anonymous public calls (a tokenless
// GET /qlink/questions/{id}, client-config signed-out). So the guard only acts
// when every gate holds, and then CONFIRMS with GET /qlearn/user/me: that
// route is plain `authenticated()` with no business 401, so a 401 there means
// the server no longer accepts the token.

// Public entry routes: a stranger (or a signed-in user) lands here from a
// link. Never NAVIGATE away from these — the pages own their sign-in prompts
// (the paid-test landing must stay put). A stranger sends no token, so the
// by-design tokenless 401s (GET /qlink/questions/{id}, client-config) never
// reach the confirmation at all. A token the server has CONFIRMED dead is
// still cleared here, without navigation, so the page's own signed-out path
// takes over: the notes-to-quiz claim page's Retry then sees no usable token
// and routes to /login?next=… itself (see utils/session.js on the
// /login <-> claim-link loop), and the QLink drawer shows its sign-in prompt.
const PUBLIC_ENTRY_PREFIXES = ['/question/view/', '/qlink/preview/', '/notes-to-quiz/'];
const PUBLIC_ENTRY_EXACT = ['/daily-absite-question'];

// Sign-in and public-info surfaces: clearing a dead token here is fine, but
// navigating away would yank a user out of registration, a password reset,
// the OAuth return or a policy page.
const NO_REDIRECT_SURFACES = [
    '/login',
    '/register',
    '/forget-password',
    '/reset-password',
    '/verify-user',
    '/socialAuth',
    '/privacy-policy',
    '/terms',
    '/support',
    '/absite-program-code-request',
    // Account deletion: a 401 while confirming can mean the account was just deleted (the
    // screen decides what to show), and after deletion the app lands on /account-deleted.
    '/user-settings/delete-account',
    '/account-deleted',
];

// Session identity only. Attribution, referral, OAuth-return, QLink share and
// pending-email-verify keys are deliberately left alone (a full clear() would
// destroy acquisition attribution for the next sign-in).
export const STALE_SESSION_KEYS = ['token', 'userId', 'role', 'userdetails', 'isProfileComplete'];

// Announced on window right after the guard clears a server-confirmed dead
// session. On a public entry route nothing navigates, so a page that read the
// login once at mount (the QLink exam page and its question container) would
// keep rendering signed-in against a cleared session: no public answer check,
// no sign-in wall, a drawer stuck on "We couldn't load the explanation". Such
// pages subscribe and switch to their signed-out path.
export const SESSION_CLEARED_EVENT = 'qlearn:session-cleared';

// Returns the unsubscribe function, for a React effect's cleanup.
export const subscribeToSessionCleared = (handler, target = typeof window === 'undefined' ? undefined : window) => {
    if (typeof handler !== 'function' || typeof target?.addEventListener !== 'function') {
        return () => {};
    }
    const listener = () => handler();
    target.addEventListener(SESSION_CLEARED_EVENT, listener);
    return () => target.removeEventListener(SESSION_CLEARED_EVENT, listener);
};

// A token the server just accepted is not re-probed for this long, so a page
// that repeatedly hits a business-401 endpoint costs one probe, not one per call.
export const LIVE_TOKEN_TTL_MS = 60 * 1000;

// Lower-cased: React Router v5 matches routes case-insensitively, so
// /Question/View/x renders the QLink page and must classify as one.
const normalizePath = (pathname) => {
    if (typeof pathname !== 'string' || pathname === '') return '/';
    const lower = pathname.toLowerCase();
    let end = lower.length;
    while (end > 1 && lower[end - 1] === '/') end -= 1;
    return lower.slice(0, end);
};

const matchesSurface = (path, surface) => {
    const target = surface.toLowerCase();
    return path === target || path.startsWith(`${target}/`);
};

export const isPublicEntryRoute = (pathname) => {
    const path = normalizePath(pathname);
    return PUBLIC_ENTRY_EXACT.some((surface) => matchesSurface(path, surface))
        || PUBLIC_ENTRY_PREFIXES.some((prefix) => `${path}/`.startsWith(prefix.toLowerCase()));
};

export const isNoRedirectSurface = (pathname) => {
    const path = normalizePath(pathname);
    return NO_REDIRECT_SURFACES.some((surface) => matchesSurface(path, surface));
};

// Only the pathname rides ?next= — query strings here can carry share tokens
// and OAuth grants — and only when /login will honor it (the same allowlist
// getSafeNextPath applies). Any other path would be discarded after sign-in
// anyway, while still landing in analytics/session-replay /login URLs
// (e.g. /mentorships/students/<another user's id>).
export const buildLoginRedirectPath = (pathname) => (
    POST_LOGIN_PATHS.includes(pathname)
        ? `/login?next=${encodeURIComponent(pathname)}`
        : '/login'
);

// If the /login navigation is cancelled (an exam page's beforeunload "Leave
// site?" answered Stay), the page keeps running tokenless; the interceptor
// re-offers the redirect on later requests, at most this often.
export const REDIRECT_REISSUE_MS = 5 * 1000;

const toAbsoluteUrl = (requestUrl, base) => {
    try {
        const raw = typeof requestUrl === 'string' ? requestUrl : requestUrl?.url || String(requestUrl);
        return new URL(raw, base);
    } catch (e) {
        return null; // unparseable: callers treat it as not-an-API request
    }
};

const safeGet = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch (e) {
        return null;
    }
};

/**
 * Builds the response observer the fetch interceptor calls for every request
 * that carried a stored token. All dependencies are injected so the gating is
 * unit-testable without a browser.
 *
 * @param {object} deps
 * @param {string} deps.probeUrl absolute GET /qlearn/user/me URL; its origin is the QLearn API origin.
 * @param {Function} deps.fetchImpl the ORIGINAL (unpatched) fetch.
 * @param {Storage} deps.storage localStorage.
 * @param {Function} deps.getPathname returns the current location pathname.
 * @param {Function} deps.redirectToLogin performs the navigation to the
 *   /login target it is given (carrying ?next= for the current pathname).
 * @param {string} [deps.baseUrl] base for relative request URLs.
 * @param {Function} [deps.onCleared] called once per clear, after the keys are
 *   removed and any /login navigation was issued (store.js announces
 *   SESSION_CLEARED_EVENT from it).
 * @param {Function} [deps.now] clock, for the live-token cache.
 * @returns {{observe: Function, isRedirecting: Function, reissueRedirect: Function,
 *   passThroughAfterClear: Function, watch: Function}} observe(response,
 *   {requestUrl, sentToken}) -> Promise<boolean> resolving true only when the
 *   session was cleared; isRedirecting() is true once the /login navigation
 *   has been issued (the interceptor must not start its own clear/redirect
 *   in the window before that navigation commits).
 */
export const createStaleSessionGuard = ({
    probeUrl,
    fetchImpl,
    storage,
    getPathname,
    redirectToLogin,
    // The /login address for the page being left; the default keeps only an
    // allow-listed pathname (buildLoginRedirectPath). store.js widens it for
    // Create Quiz: a rebuilt ?next= (/create-quiz, or the practice entry
    // /create-quiz?practice=absite) plus the campaign keys and ad click ids.
    loginRedirectPath = buildLoginRedirectPath,
    baseUrl,
    onCleared,
    now = () => Date.now(),
}) => {
    const probe = toAbsoluteUrl(probeUrl, baseUrl);
    const apiOrigin = probe ? probe.origin : null;
    const probePath = probe ? normalizePath(probe.pathname) : null;

    let inflight = null; // { token, promise }
    let liveToken = null; // { token, at }
    let redirected = false;
    let redirectTarget = null;
    let redirectIssuedAt = 0;

    const confirmRejected = (sentToken) => {
        if (liveToken && liveToken.token === sentToken && now() - liveToken.at < LIVE_TOKEN_TTL_MS) {
            return Promise.resolve(false);
        }
        if (inflight && inflight.token === sentToken) return inflight.promise;

        const promise = Promise.resolve()
            .then(() => fetchImpl(probe.href, {
                method: 'GET',
                headers: { Authorization: `Bearer ${sentToken}` },
            }))
            .then((res) => {
                const rejected = res?.status === 401;
                if (!rejected && res?.ok) {
                    liveToken = { token: sentToken, at: now() };
                }
                return rejected;
            }, () => false)
            .finally(() => {
                if (inflight?.promise === promise) inflight = null;
            });
        inflight = { token: sentToken, promise };
        return promise;
    };

    // The stored token must still be the one the server rejected: a newer
    // sign-in (or an earlier clear) wins.
    const stillCurrent = (sentToken) => safeGet(storage, 'token') === sentToken;

    const clearAndRedirect = () => {
        STALE_SESSION_KEYS.forEach((key) => {
            try {
                storage.removeItem(key);
            } catch (e) {
                // Storage blocked: nothing more we can do; never throw into fetch callers.
            }
        });
        const path = getPathname();
        if (!redirected && !isPublicEntryRoute(path) && !isNoRedirectSurface(path)) {
            redirected = true;
            redirectTarget = loginRedirectPath(path);
            redirectIssuedAt = now();
            try {
                redirectToLogin(redirectTarget);
            } catch (e) {
                // Navigation failure must not surface as a fetch error.
            }
        }
        // Last, so a request a listener fires sees the redirect already issued
        // (passThroughAfterClear) instead of the interceptor's legacy full clear.
        if (typeof onCleared === 'function') {
            try {
                onCleared();
            } catch (e) {
                // A listener failure must not surface as a fetch error.
            }
        }
    };

    const observe = (response, { requestUrl, sentToken } = {}) => {
        try {
            if (response?.status !== 401) return Promise.resolve(false);
            if (typeof sentToken !== 'string' || sentToken === '') return Promise.resolve(false);
            if (!apiOrigin) return Promise.resolve(false);
            const target = toAbsoluteUrl(requestUrl, baseUrl);
            if (target?.origin !== apiOrigin) return Promise.resolve(false);
            if (!stillCurrent(sentToken)) return Promise.resolve(false);

            const confirmation = normalizePath(target.pathname) === probePath
                ? Promise.resolve(true)
                : confirmRejected(sentToken);

            return confirmation.then((rejected) => {
                // Re-check after the async probe: the user may have signed in again
                // (a new token) meanwhile. The route is read at clear time, so a
                // move onto a public page during the probe suppresses navigation.
                if (!rejected || !stillCurrent(sentToken)) return false;
                clearAndRedirect();
                return true;
            }).catch(() => false);
        } catch (e) {
            return Promise.resolve(false); // never throw into fetch callers
        }
    };

    // Called by the interceptor for requests made after the clear: re-issues
    // the same /login navigation when the first one did not commit. The route
    // is re-read: after "Stay" the user may have moved in-app onto a public or
    // sign-in surface, which the first redirect would also have spared.
    const reissueRedirect = () => {
        if (!redirected || now() - redirectIssuedAt < REDIRECT_REISSUE_MS) return false;
        const path = getPathname();
        if (isPublicEntryRoute(path) || isNoRedirectSurface(path)) return false;
        redirectIssuedAt = now();
        try {
            redirectToLogin(redirectTarget);
        } catch (e) {
            // Navigation failure must not surface as a fetch error.
        }
        return true;
    };

    // Interceptor hook for a tokenless request after the guard's own clear:
    // true = send it as-is (the interceptor must not start its legacy full
    // clear + "/" redirect, which would wipe keys the guard preserves), and
    // re-offer /login in case the first navigation was cancelled.
    const passThroughAfterClear = (token) => {
        if (token || !redirected) return false;
        reissueRedirect();
        return true;
    };

    // Attaches the observer to a request sent with the stored token and returns
    // the caller's original promise untouched.
    const watch = (pending, { requestUrl, sentToken } = {}) => {
        if (sentToken && typeof pending?.then === 'function') {
            pending.then((response) => observe(response, { requestUrl, sentToken }), () => {});
        }
        return pending;
    };

    return { observe, isRedirecting: () => redirected, reissueRedirect, passThroughAfterClear, watch };
};
